import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { 
  Mail, Send, FileText, Users, TrendingUp, CheckCircle, XCircle, 
  MailPlus, RefreshCw, Clock, AlertCircle 
} from 'lucide-react';
import axios from 'axios';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { user, isAdmin, isManager } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [myEmails, setMyEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailsLoading, setEmailsLoading] = useState(true);

  const fetchStats = async () => {
    if (isManager()) {
      try {
        const response = await axios.get(`${API}/stats`);
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      }
    }
    setLoading(false);
  };

  const fetchMyEmails = async () => {
    try {
      const response = await axios.get(`${API}/emails/my-logs?limit=10`);
      setMyEmails(response.data);
    } catch (error) {
      console.error('Failed to fetch my emails:', error);
    } finally {
      setEmailsLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchMyEmails();
    
    // Auto-refresh every 30 seconds for real-time updates
    const interval = setInterval(fetchMyEmails, 30000);
    return () => clearInterval(interval);
  }, []);

  const StatCard = ({ icon: Icon, label, value, description, color = "primary" }) => (
    <Card className="hover:border-primary/50 transition-colors">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
        <Icon className={`w-4 h-4 text-${color}`} />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
      </CardContent>
    </Card>
  );

  const getStatusBadge = (status) => {
    if (status === 'sent') {
      return (
        <Badge className="bg-green-500/10 text-green-600 hover:bg-green-500/20 border-green-500/20">
          <CheckCircle className="w-3 h-3 mr-1" />
          Delivered
        </Badge>
      );
    }
    return (
      <Badge variant="destructive" className="bg-red-500/10 text-red-600 hover:bg-red-500/20 border-red-500/20">
        <XCircle className="w-3 h-3 mr-1" />
        Failed
      </Badge>
    );
  };

  const getTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const showSentByColumn = isManager();

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          Welcome back, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-muted-foreground mt-1">
          {user?.role === 'admin' && 'You have full access to all features'}
          {user?.role === 'manager' && 'Manage senders, templates and monitor team activity'}
          {user?.role === 'agent' && 'Send emails to customers and track delivery status'}
        </p>
      </div>

      {/* Stats for Manager/Admin */}
      {isManager() && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {loading ? (
            <>
              {[...Array(4)].map((_, i) => (
                <Card key={i}>
                  <CardHeader className="pb-2">
                    <Skeleton className="h-4 w-24" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-8 w-16" />
                  </CardContent>
                </Card>
              ))}
            </>
          ) : stats ? (
            <>
              <StatCard
                icon={Mail}
                label="Total Emails"
                value={stats.total_emails}
                description="All time sent emails"
              />
              <StatCard
                icon={CheckCircle}
                label="Success Rate"
                value={`${stats.success_rate}%`}
                description={`${stats.sent_emails} delivered`}
              />
              <StatCard
                icon={FileText}
                label="Templates"
                value={stats.total_templates}
                description="Active templates"
              />
              <StatCard
                icon={MailPlus}
                label="Senders"
                value={stats.total_senders}
                description="Configured senders"
              />
            </>
          ) : null}
        </div>
      )}

      {/* Recent Emails Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              {showSentByColumn ? 'Recent Emails (All Team)' : 'My Recent Emails'}
            </CardTitle>
            <CardDescription>
              {showSentByColumn 
                ? 'Track delivery status of all emails sent by your team'
                : 'Track the delivery status of emails you\'ve sent'}
            </CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchMyEmails}
            data-testid="refresh-my-emails"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {emailsLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : myEmails.length === 0 ? (
            <div className="py-12 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No emails sent yet</h3>
              <p className="text-muted-foreground mb-4">
                {showSentByColumn 
                  ? 'No emails have been sent by the team yet'
                  : 'Start sending emails to see their delivery status here'}
              </p>
              <Button onClick={() => navigate('/send-email')}>
                <Send className="w-4 h-4 mr-2" />
                Send Email
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Recipient</TableHead>
                  <TableHead>Template</TableHead>
                  {showSentByColumn && <TableHead>Sent By</TableHead>}
                  <TableHead>Sent</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {myEmails.map(email => (
                  <TableRow key={email.id} data-testid={`my-email-${email.id}`}>
                    <TableCell>{getStatusBadge(email.status)}</TableCell>
                    <TableCell className="font-mono text-sm">{email.recipient_email}</TableCell>
                    <TableCell>{email.template_name}</TableCell>
                    {showSentByColumn && <TableCell className="text-sm">{email.sent_by_name}</TableCell>}
                    <TableCell className="text-muted-foreground text-sm">
                      {getTimeAgo(email.sent_at)}
                    </TableCell>
                    <TableCell>
                      {email.status === 'failed' && email.error_message && (
                        <div className="flex items-center gap-1 text-red-600 text-xs">
                          <AlertCircle className="w-3 h-3" />
                          <span className="truncate max-w-[150px]" title={email.error_message}>
                            {email.error_message}
                          </span>
                        </div>
                      )}
                      {email.status === 'sent' && (
                        <span className="text-green-600 text-xs">Email delivered successfully</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Card 
            className="cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => navigate('/send-email')}
            data-testid="quick-send-email"
          >
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-primary/10">
                  <Send className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-base">Send Email</CardTitle>
                  <CardDescription>Send an email to a customer</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          {isManager() && (
            <>
              <Card 
                className="cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => navigate('/templates')}
                data-testid="quick-templates"
              >
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-md bg-primary/10">
                      <FileText className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-base">Manage Templates</CardTitle>
                      <CardDescription>Create or edit email templates</CardDescription>
                    </div>
                  </div>
                </CardHeader>
              </Card>

              <Card 
                className="cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => navigate('/senders')}
                data-testid="quick-senders"
              >
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-md bg-primary/10">
                      <MailPlus className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-base">Manage Senders</CardTitle>
                      <CardDescription>Configure sender email addresses</CardDescription>
                    </div>
                  </div>
                </CardHeader>
              </Card>

              <Card 
                className="cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => navigate('/logs')}
                data-testid="quick-logs"
              >
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-md bg-primary/10">
                      <TrendingUp className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-base">Email Performance</CardTitle>
                      <CardDescription>View delivery stats and logs</CardDescription>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            </>
          )}

          {isAdmin() && (
            <Card 
              className="cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => navigate('/users')}
              data-testid="quick-users"
            >
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-md bg-primary/10">
                    <Users className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-base">Manage Users</CardTitle>
                    <CardDescription>Add or manage team members</CardDescription>
                  </div>
                </div>
              </CardHeader>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
